import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getPostLoginPath } from '../utils/postLoginRoute';
import { resolveApiBase } from '../utils/httpClient';

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  padding: clamp(1rem, 4vw, 2rem);
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  padding: clamp(1.5rem, 4vw, 2.5rem);
  width: 100%;
  max-width: 460px;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  color: #0f766e;
  text-align: center;
`;

const Body = styled.p`
  margin: 0 0 1.25rem;
  color: #475569;
  line-height: 1.55;
  text-align: center;
`;

const Notice = styled.div`
  background: ${(p) => (p.$error ? '#fef2f2' : '#ecfdf5')};
  border: 1px solid ${(p) => (p.$error ? '#fecaca' : '#99f6e4')};
  color: ${(p) => (p.$error ? '#b91c1c' : '#115e59')};
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: 600;
  text-align: center;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #334155;
  input {
    display: block;
    width: 100%;
    margin-top: 0.35rem;
    padding: 0.65rem 0.75rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font: inherit;
  }
`;

const Hint = styled.p`
  margin: -0.5rem 0 1rem;
  font-size: 0.8rem;
  color: #64748b;
`;

const Button = styled.button`
  width: 100%;
  background: #0f766e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }
`;

const Links = styled.div`
  text-align: center;
  margin-top: 1.25rem;
  color: #64748b;
  a {
    color: #0f766e;
    font-weight: 600;
    text-decoration: none;
  }
`;

const ResendInvite = ({ intro }) => {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setFailed(true);
      setNote('Enter the email address the invite was sent to.');
      return;
    }
    setSending(true);
    setNote('');
    try {
      const { data } = await axios.post(`${resolveApiBase()}/api/auth/invite/resend/`, {
        email: email.trim(),
      });
      setFailed(false);
      setNote(data?.message || 'If that address has an unclaimed account, a new link is on its way.');
    } catch (err) {
      setFailed(true);
      setNote(err.response?.data?.error || 'Could not send a new link. Try again in a minute.');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <Body style={{ textAlign: 'left', marginBottom: '0.75rem' }}>{intro}</Body>
      <Field>
        Email
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
        />
      </Field>
      <Button type="submit" disabled={sending}>
        {sending ? 'Sending…' : 'Send me a new invite link'}
      </Button>
      {note ? (
        <Notice $error={failed} style={{ marginTop: '0.75rem' }}>
          {note}
        </Notice>
      ) : null}
    </form>
  );
};

const SetPassword = () => {
  const { claimInvite } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = (searchParams.get('uid') || '').trim();
  const token = (searchParams.get('token') || '').trim();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [linkDead, setLinkDead] = useState(!uid || !token);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }
    setSaving(true);
    setError('');
    const result = await claimInvite({ uid, token, password, confirmPassword });
    setSaving(false);
    if (result.success) {
      navigate(getPostLoginPath(result.user), { replace: true });
      return;
    }
    if (result.code === 'invite_invalid') {
      setLinkDead(true);
      return;
    }
    setError(result.error);
  };

  return (
    <Shell>
      <Card>
        <Title>{linkDead ? 'Get a new invite link' : 'Set your password'}</Title>
        {linkDead ? (
          <>
            <ResendInvite
              intro={
                uid || token
                  ? 'This link has expired or was already used. We can email you a fresh one.'
                  : 'A consultancy or institution added you to EnterWeU? Enter the email they used and we will send a link to set your password.'
              }
            />
          </>
        ) : (
          <>
            <Body>
              Someone added you to EnterWeU. Choose a password to claim your account. Nobody else
              has seen or set it.
            </Body>
            {error ? <Notice $error>{error}</Notice> : null}
            <form onSubmit={onSubmit}>
              <Field>
                New password
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  maxLength={128}
                />
              </Field>
              <Hint>At least 8 characters, not only numbers.</Hint>
              <Field>
                Confirm password
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  maxLength={128}
                />
              </Field>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Set password and sign in'}
              </Button>
            </form>
          </>
        )}
        <Links>
          <Link to="/login">Back to sign in</Link>
        </Links>
      </Card>
    </Shell>
  );
};

export default SetPassword;
